import { SearchIcon } from "@heroicons/react/solid";
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import { IEventIdsItem } from "interfaces";
import { isEmpty } from "lodash";
import moment from "moment";
import { useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import {
  EWAIVER_STATUS,
  PARTICIPANT_IMPORT_SOURCES,
  STATUS_OPTIONS,
} from "utills/constants";
import { twMerge } from "tailwind-merge";

export const Filter = ({
  currentFilterValues,
  setFilters,
}: {
  currentFilterValues: any;
  setFilters: any;
}) => {
  const [keyword, setKeyword] = useState(currentFilterValues.q);
  const [eventId, setEventId] = useState(currentFilterValues.event_id);
  const [categoryId, setCategoryId] = useState(currentFilterValues.category);
  const [status, setStatus] = useState(currentFilterValues.is_active);
  const [ewaiverStatus, setEwaiverStatus] = useState(
    currentFilterValues.ewaiver_status,
  );
  const [source, setSource] = useState(currentFilterValues.source);

  const [createdDate, setCreatedDate] = useState({
    startDate: currentFilterValues.gte || "",
    endDate: currentFilterValues.lte || "",
  });

  const { data: eventsData, isFetching } = useOne<IEventIdsItem[], HttpError>({
    resource: "content/dropdown/events?uncheck=true&event_type=sports",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const { data: categoryData, isFetching: isFetchingCategoryData } = useOne<
    any[],
    HttpError
  >({
    resource: `participant/events/${eventId}/categories/dropdown`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventId,
    } as any,
  });

  const eventIds = (eventsData?.data ?? []).map((event: IEventIdsItem) => ({
    value: event.id,
    label: event.name_vi,
  }));

  const categoryIds = (!isEmpty(eventId) ? (categoryData?.data ?? []) : []).map(
    (category: { category_vi: string; category_en: string }) => ({
      value: category.category_en,
      label: category.category_en,
    }),
  );

  const _handleSubmitFilter = () => {
    setFilters([
      { field: "q", operator: "contains", value: keyword },
      { field: "event_id", operator: "eq", value: eventId },
      { field: "is_active", operator: "eq", value: status },
      { field: "category", operator: "eq", value: categoryId },
      { field: "ewaiver_status", operator: "eq", value: ewaiverStatus },
      { field: "source", operator: "eq", value: source },
      {
        field: "gte",
        operator: "eq",
        value: createdDate.startDate
          ? moment(createdDate.startDate).format("YYYY-MM-DD")
          : "",
      },
      {
        field: "lte",
        operator: "eq",
        value: createdDate.endDate
          ? moment(createdDate.endDate).format("YYYY-MM-DD")
          : "",
      },
      { field: "page", operator: "eq", value: 1 },
    ]);
  };

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label htmlFor="name" className="block text-sm font-medium leading-6">
            Search
          </label>
          <div className="mt-2">
            <Input
              className="px-2 py-2"
              label="Search"
              id="name"
              type="text"
              placeholder="Search by Participant ID, Full name, Email, Phone, ID card, Order code, BIB"
              value={keyword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setKeyword(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  _handleSubmitFilter();
                }
              }}
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="event_id"
            className="block text-sm font-medium leading-6"
          >
            Event name
          </label>
          <div className="mt-2">
            <Select
              options={eventIds}
              isLoading={isFetching}
              isClearable={true}
              id="event_id"
              placeholder="Select Event"
              className="py-0.5"
              value={eventIds.find((d) => d.value === eventId)}
              onChange={(selected: { label: string; value: string }) => {
                setEventId(selected?.value);
                setCategoryId("");
              }}
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="category"
            className="block text-sm font-medium leading-6"
          >
            Category
          </label>
          <div className="mt-2">
            <Select
              options={categoryIds}
              isLoading={isFetchingCategoryData}
              isClearable={true}
              key={`category__${eventId ?? "all"}`}
              id="category"
              placeholder="Select Category"
              className="py-0.5"
              value={categoryIds.find((d) => d.value === categoryId)}
              onChange={(selected: { label: string; value: string }) =>
                setCategoryId(selected?.value)
              }
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
        <div className="flex-1">
          <label
            htmlFor="is_active"
            className="block text-sm font-medium leading-6"
          >
            Participant status
          </label>
          <div className="mt-2">
            <Select
              options={STATUS_OPTIONS}
              isClearable={true}
              id="is_active"
              placeholder="Select Status"
              className="py-0.5"
              value={STATUS_OPTIONS.find((d) => d.value === status)}
              onChange={(selected: { label: string; value: string }) =>
                setStatus(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="ewaiver_status"
            className="block text-sm font-medium leading-6"
          >
            Ewaiver status
          </label>
          <div className="mt-2">
            <Select
              options={EWAIVER_STATUS}
              isClearable={true}
              id="ewaiver_status"
              placeholder="Select Ewaiver Status"
              className="py-0.5"
              value={EWAIVER_STATUS.find((d) => d.value === ewaiverStatus)}
              onChange={(selected: { label: string; value: string }) =>
                setEwaiverStatus(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="source"
            className="block text-sm font-medium leading-6"
          >
            Source
          </label>
          <div className="mt-2">
            <Select
              options={PARTICIPANT_IMPORT_SOURCES}
              isClearable={true}
              id="source"
              placeholder="Select Source"
              className="py-0.5"
              value={PARTICIPANT_IMPORT_SOURCES.find(
                (d) => d.value === source,
              )}
              onChange={(selected: { label: string; value: string }) =>
                setSource(selected?.value)
              }
            />
          </div>
        </div>
        <div className="flex-1">
          <label
            htmlFor="created_date"
            className="block text-sm font-medium leading-6"
          >
            Created Date
          </label>
          <div className="mt-2">
            <Datepicker
              key={"created_date"}
              value={createdDate}
              onChange={(value) =>
                setCreatedDate({
                  startDate: value?.startDate
                    ? moment(value?.startDate).format("YYYY-MM-DD")
                    : null,
                  endDate: value?.endDate
                    ? moment(value?.endDate).format("YYYY-MM-DD")
                    : null,
                })
              }
              displayFormat="DD/MM/YYYY"
              placeholder="Start date - End date"
              inputClassName="date-picker"
              popoverDirection="down"
              containerClassName={(className) => twMerge(className, "date-picker-container")}
            /> 
          </div>
        </div>
        <div className="shrink-0">
          <label
            htmlFor="status"
            className="block text-sm font-medium leading-6"
          >
            <span className="sr-only">Search</span>
          </label>
          <div className="mt-2 w-full">
            <Button
              type="submit"
              className="w-full bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
              onClick={_handleSubmitFilter}
            >
              <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
